import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  nomenclatureGroups,
  type NomenclatureGroup,
} from '@/api/nomenclatureGroups';
import { useConfirm } from '@/components/ConfirmProvider';
import { usePermissions } from '@/auth/usePermissions';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

/**
 * Довідник «Номенклатурні групи» (ч.649) — одиниці обліку НЗВ
 * (аналітика рахунку 23). Iмпортуються з 1С; юзер може створювати/
 * перейменовувати/видаляти (мутації — за refs.nomenclature.edit).
 */
export default function NomenclatureGroupsPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canEdit = has('refs.nomenclature.edit');
  const [newName, setNewName] = useState('');
  const [newCode, setNewCode] = useState('');
  const [showNew, setShowNew] = useState(false);

  const list = useQuery({
    queryKey: ['nomenclature-groups'],
    queryFn: nomenclatureGroups.list,
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['nomenclature-groups'] });

  const create = useMutation({
    mutationFn: () =>
      nomenclatureGroups.create({
        name: newName.trim(),
        code: newCode.trim() || null,
      }),
    onSuccess: () => {
      setNewName('');
      setNewCode('');
      setShowNew(false);
      invalidate();
    },
  });
  const update = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      nomenclatureGroups.update(id, { name }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => nomenclatureGroups.remove(id),
    onSuccess: invalidate,
  });

  const rows = list.data ?? [];

  const cols = useMemo<ColumnDef<NomenclatureGroup>[]>(() => [
    {
      key: 'code', header: 'Код', width: 140,
      sortValue: (g) => g.code ?? '',
      cell: (g) => g.code ?? '—',
    },
    {
      key: 'name', header: 'Назва',
      sortValue: (g) => g.name.toLowerCase(),
      cell: (g) => (
        <>
          {canEdit ? (
            <input
              defaultValue={g.name}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== g.name) update.mutate({ id: g.id, name: v });
              }}
              style={{ width: '85%' }}
            />
          ) : (
            g.name
          )}
          {g.external_source && (
            <span
              title="Імпортовано з 1С"
              style={{
                color: '#2563eb',
                border: '1px solid var(--badge-blue-bd)',
                borderRadius: 4,
                fontSize: '0.7rem',
                fontWeight: 700,
                padding: '0 4px',
                marginLeft: 6,
              }}
            >
              I
            </span>
          )}
        </>
      ),
    },
    {
      key: 'actions', header: '', width: 100, align: 'center',
      cell: (g) => (
        <button
          className="danger"
          title="Видалити групу"
          disabled={!canEdit}
          onClick={() =>
            confirm(
              g.external_source
                ? `Групу «${g.name}» імпортовано з 1С. Видалити її?`
                : `Видалити групу «${g.name}»?`,
            ).then((ok) => {
              if (ok) remove.mutate(g.id);
            })
          }
        >
          ✕
        </button>
      ),
    },
  ], [canEdit, update, remove, confirm]);

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Номенклатурні групи</h2>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          disabled={!canEdit}
          title={canEdit ? undefined : 'Немає права refs.nomenclature.edit'}
          onClick={() => setShowNew((v) => !v)}
        >
          + Нова група
        </button>
      </div>
      <p style={{ color: 'var(--muted)', maxWidth: 720 }}>
        Одиниці обліку НЗВ (аналітика рахунку 23). Імпортуються з 1С як
        довідник; використовуються у виробничих документах і закритті місяця.
      </p>

      {showNew && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', margin: '1rem 0' }}>
          <input
            placeholder="Код (опц.)"
            value={newCode}
            onChange={(e) => setNewCode(e.target.value)}
            style={{ width: 120 }}
          />
          <input
            placeholder="Назва нової групи"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            style={{ minWidth: 240 }}
          />
          <button
            className="primary"
            disabled={!canEdit || !newName.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Додати
          </button>
        </div>
      )}

      <div style={{ maxWidth: 720 }}>
        <DataTable<NomenclatureGroup>
          rows={rows}
          columns={cols}
          rowKey={(g) => g.id}
          loading={list.isLoading}
          emptyText="Немає номенклатурних груп."
          initialSortKey="name"
        />
      </div>
    </div>
  );
}
